import styled from "@emotion/styled";
import { Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import { useExportTrialBalance, useTrialBalances } from "features/finances";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EthiopianDateField } from "../../components/EthiopianDateField";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

export const GeneralLedgerTable = () => {
  const { t } = useTranslation();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const navigate = useNavigate();

  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  const { data: trialBalances, isLoading } = useTrialBalances({
    churchId: selectedChurch?.id,
    startDate: filterDates.startDate,
    endDate: filterDates.endDate
  });

  const { mutate: exportTrialBalance, isLoading: isExporting } =
    useExportTrialBalance();

  const summaryData = {
    totalDebits: trialBalances?.totals?.total_debits || 0,
    totalCredits: trialBalances?.totals?.total_credits || 0
  };

  const ledgerData = trialBalances?.data || [];

  const columns = [
    {
      field: "account_number",
      headerName: "Account ID",
      flex: 1
    },
    {
      field: "account_name",
      headerName: "Account Name",
      flex: 1
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1
    },
    {
      field: "total_debits",
      headerName: "Debit",
      flex: 1,
      valueGetter: ({ row }) =>
        Number(row.total_debits) === 0
          ? ""
          : Number(row.total_debits).toLocaleString()
    },
    {
      field: "total_credits",
      headerName: "Credit",
      flex: 1,
      valueGetter: ({ row }) =>
        Number(row.total_credits) === 0
          ? ""
          : Number(row.total_credits).toLocaleString()
    }
  ];

  const rowsWithTotal = useMemo(() => {
    return [
      ...ledgerData,
      {
        id: "total",
        account_number: "",
        account_name: "",
        description: "Total",
        total_debits: summaryData.totalDebits,
        total_credits: summaryData.totalCredits
      }
    ];
  }, [ledgerData, summaryData]);

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" mb={3} spacing={2}>
          <EthiopianDateField
            size="small"
            label="Start date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <EthiopianDateField
            size="small"
            label="End date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
          <Button onClick={handleFilter}>Filter</Button>
        </Stack>
        <Button
          onClick={() =>
            exportTrialBalance({
              churchId: selectedChurch?.id,
              queries: {
                startDate: filterDates.startDate,
                endDate: filterDates.endDate
              }
            })
          }
          disabled={isExporting}
        >
          Export
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        rows={rowsWithTotal}
        loading={isLoading}
        columns={columns}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        onRowClick={({ row }) => {
          if (row.id === "total") return;

          navigate(
            `/finances/temp/chart-of-accounts/${row.id}?accountName=${
              row.account_name
            }&accountNumber=${row.account_number}&accountType=${
              row.account_type || ""
            }`
          );
        }}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows")
        }}
        sx={{
          m: 0,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#E6F7F8",
            borderRadius: "10px 10px 0px 0px",
            color: "#000000",
            border: "none"
          },
          "& .MuiDataGrid-row:last-child": {
            fontWeight: "bold"
          }
        }}
      />
    </Container>
  );
};
